import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, MailCheck } from 'lucide-react';
import { FormField, PrimaryButton, SecondaryButton, ErrorAlert } from '../../../shared/components';
import { useRequestEmailChange } from '../hooks';
import { useValidation } from '../../../shared/hooks';
import { useAuth } from '../../../shared/hooks/useAuth';

export const ChangeEmailModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_email: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const { data: user } = useAuth({ enabled: true });
  const { errors, validateFieldOnBlur, validateForm, clearFieldError, clearAllErrors } = useValidation();
  
  const { mutate: requestEmailChange, isPending, error, reset } = useRequestEmailChange(
    () => {
      setFormData({
        current_password: '',
        new_email: ''
      });
      clearAllErrors();
    },
    (data) => {      
      // Use API response data if available, otherwise fall back to form data
      const newEmail = data?.data?.data?.new_email || formData.new_email;
      setSuccessData({ new_email: newEmail });
      setIsSuccess(true);
    },

  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    clearFieldError(name);
    
    // Clear API errors when user types (same as ChangePasswordModal)
    reset();
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === 'new_email') {
      // Pass current email for validation
      validateFieldOnBlur(name, value, { currentEmail: user?.email });
    } else {
      validateFieldOnBlur(name, value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      return;
    }

    requestEmailChange(formData);
  };

  const handleCancel = () => {
    onClose();
  };

  // Success state display
  if (isSuccess) {
    return (
      <div className="space-y-6">
        {/* Success Header */}
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Email Change Requested Successfully
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            We've sent a verification email to <span className="font-semibold text-gray-900">{successData?.new_email}</span> to complete your email change.
          </p>
          <MailCheck className="h-16 w-16 text-success-500 mx-auto mb-4" />
        </div>

        {/* Important Notes */}
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <p className="text-sm text-primary-800">
            <strong>Important:</strong> The verification link will expire in 24 hours. 
            If you don't verify within this time, you'll need to submit a new request.
          </p>
        </div>

        <p className="text-sm text-gray-600">
            We have also sent an email to your current email address for your security.
        </p>

        {/* Close Button */}
        <div className="pt-4">
          <SecondaryButton
            type="button"
            onClick={handleCancel}
            className="w-full"
          >
            Close
          </SecondaryButton>
        </div>
      </div>
    );
  }

  // Form state display
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-left">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Change Email Address
        </h3>
        <p className="text-sm text-gray-600">
            Enter your current password and the new email address you'd like to use.
        </p>
      </div>

      {/* General Error Display */}
      <ErrorAlert
        error={error?.response?.data?.message || error?.message}
        onClose={() => reset()}
        show={!!error && !error?.response?.data?.errors}
        className="mb-4"
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Password Field */}
        <FormField
          label=""
          name="current_password"
          type={showCurrentPassword ? 'text' : 'password'}
          value={formData.current_password}
          onChange={handleChange}
          onBlur={handleFieldBlur}
          placeholder="Current Password"
          required
          autoComplete="off"
          error={errors.current_password || error?.response?.data?.errors?.current_password}
          icon={<Lock className="h-5 w-5" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          }
          className="[&_input]:text-sm [&_label]:text-xs [&_.text-sm]:text-xs"
        />

        <div className="py-2">
            <hr className="border-gray-200" />
        </div>

        {/* New Email Field */}
        <FormField
          label=""
          name="new_email"
          type="email"
          value={formData.new_email}
          onChange={handleChange}
          onBlur={handleFieldBlur}
          placeholder="New Email Address"
          required
          autoComplete="off"
          error={errors.new_email || error?.response?.data?.errors?.new_email}
          icon={<Mail className="h-5 w-5" />}
          className="[&_input]:text-sm [&_label]:text-xs [&_.text-sm]:text-xs"
        />

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <SecondaryButton 
            type="button" 
            onClick={handleCancel} 
            className="flex-1" 
            disabled={isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton 
            type="submit" 
            className="flex-1" 
            disabled={isPending || Object.values(errors).some(error => error && error.trim() !== '')}
          >
            {isPending ? 'Requesting...' : 'Request'}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
};
